import type { HelpAnswer, HelpConversation, HelpMessage, HelpTopics } from '@myrewards/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { Button as AriaButton, Form, Link } from 'react-aria-components';
import {
  aiKeys,
  askHelp,
  clearHelpHistory,
  helpConversationQuery,
  helpTopicsQuery,
  rateHelpAnswer,
  sendHelpHandoff,
} from '../api/ai';
import { MicHelp } from '../feedback/voice/MicHelp';
import { namesInWords } from '../shell/HelpPanel';
import { ShowMeButton } from '../shell/ShowMeButton';
import type { HelpAnchorRef, HelpTopic } from '../shell/types';
import { Button, ButtonLink, cx, Icon, TextField } from '../ui';
import { aiProblemWords, isAiOff } from './aiWords';
import { DictateButton, DictationStrip, useHelpDictation } from './HelpDictation';
import s from './HelpChat.module.css';

export interface HelpChatProps {
  /** The Help content's key for the page ("home"); answers are about this page. */
  pageKey: string;
  greetingName: string;
  initials: string;
  /** Questions to start with on this page. */
  suggestions: readonly string[];
  /** For the "how I got this" topic chips under an answer. */
  topics: readonly HelpTopic[];
  contact: HelpTopics['contact'] | null;
  /** Requests left today across every AI feature; null when unlimited. */
  remainingToday: number | null;
  openTopic: (id: string) => void;
  /** "Show me" (APP-090): points at something on the page; false when it isn't there. */
  showMe?: (anchor: HelpAnchorRef) => boolean;
  /** The feature was switched off while the panel was open: the shell refreshes. */
  onSwitchedOff: () => void;
  /** Voice for Help chat is offered to me now (APP-124): the microphone beside Ask. */
  voiceOffered?: boolean;
  /** Voice time left today, in seconds (shared with feedback); null when unknown. */
  voiceSecondsLeft?: number | null;
}

const SUGGESTION_LIMIT = 5;
/** The longest question (`HelpAsk`). */
const QUESTION_MAX = 2000;
const TOO_LONG = 'Your question is over 2,000 characters. Shorten it before you ask.';

/** Dictated words after what's already in the box, as one question. */
function withWords(typed: string, words: string): string {
  return typed.trim() === '' ? words : `${typed.trimEnd()} ${words}`;
}

function replaceMessage(convo: HelpConversation, message: HelpMessage): HelpConversation {
  return { ...convo, messages: convo.messages.map((m) => (m.id === message.id ? message : m)) };
}

/**
 * Help chat (AI4): answers about MyRewards and the page the person is on, from the help topics
 * and only what they can already see. It never acts; links take them to the right screen, and a
 * message to the Stock Admin team is drafted, shown and sent only when they press Send.
 */
export function HelpChat({
  pageKey,
  greetingName,
  initials,
  suggestions,
  topics,
  contact,
  remainingToday,
  openTopic,
  showMe,
  onSwitchedOff,
  voiceOffered = false,
  voiceSecondsLeft = null,
}: HelpChatProps): ReactElement {
  const queryClient = useQueryClient();
  const conversation = useQuery(helpConversationQuery);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [cleared, setCleared] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  /** What was in the box when dictation began: the words go after it, and Cancel restores it. */
  const [typed, setTyped] = useState('');
  const byId = new Map(topics.map((t) => [t.id, t]));

  const dictation = useHelpDictation({
    offered: voiceOffered,
    secondsLeft: voiceSecondsLeft,
    onWords: (words) => {
      setDraft(withWords(typed, words));
      // Back to the box, at the end, to check the words and press Ask.
      window.requestAnimationFrame(() => {
        const box = formRef.current?.querySelector('textarea');
        if (box === null || box === undefined) return;
        box.focus({ preventScroll: true });
        box.setSelectionRange(box.value.length, box.value.length);
      });
    },
  });
  const dictating = dictation.phase !== 'idle';

  const ask = useMutation({
    mutationFn: async (question: string) => {
      await queryClient.query(helpTopicsQuery(pageKey));
      return askHelp(question, pageKey);
    },
    onSuccess: (reply) => {
      setError(null);
      queryClient.setQueryData<HelpConversation>(aiKeys.conversation, (current) => ({
        conversationId: reply.conversationId,
        historyDays: current?.historyDays ?? 30,
        messages: [...(current?.messages ?? []), reply.question, reply.reply],
      }));
      void queryClient.invalidateQueries({ queryKey: aiKeys.status });
    },
    onError: (failure, question) => {
      setError(aiProblemWords(failure, 'help'));
      setDraft((current) => (current === '' ? question : current));
      if (isAiOff(failure)) onSwitchedOff();
    },
  });

  const rate = useMutation({
    mutationFn: ({ id, rating }: { id: string; rating: 'up' | 'down' | null }) =>
      rateHelpAnswer(id, rating),
    onSuccess: (message) => {
      queryClient.setQueryData<HelpConversation>(aiKeys.conversation, (current) =>
        current === undefined ? current : replaceMessage(current, message),
      );
    },
    onError: (failure) => {
      setError(aiProblemWords(failure, 'help'));
    },
  });

  const clear = useMutation({
    mutationFn: clearHelpHistory,
    onSuccess: (convo) => {
      queryClient.setQueryData(aiKeys.conversation, convo);
      setConfirmClear(false);
      setCleared(true);
      setError(null);
    },
    onError: (failure) => {
      setError(aiProblemWords(failure, 'help'));
    },
  });

  const messages = conversation.data?.messages ?? [];
  const pendingQuestion = ask.isPending ? ask.variables : null;

  useEffect(() => {
    // Keep the newest message in view by scrolling the Help panel only, never the page.
    let box = endRef.current?.parentElement ?? null;
    while (box !== null && box.scrollHeight <= box.clientHeight) box = box.parentElement;
    if (box !== null && box !== document.documentElement && box !== document.body)
      box.scrollTop = box.scrollHeight;
  }, [messages.length, pendingQuestion]);

  const submit = (question: string): void => {
    const text = question.trim();
    if (text === '' || ask.isPending || dictating || text.length > QUESTION_MAX) return;
    setDraft('');
    setCleared(false);
    // APP-174: a line about the recording is about the words just sent.
    dictation.asked();
    ask.mutate(text);
  };

  const historyDays = conversation.data?.historyDays ?? 30;
  const empty = messages.length === 0 && pendingQuestion === null;

  return (
    <div className={s.chat}>
      <div className={s.bar}>
        <span className={s.private}>
          <Icon name="lock" size={14} /> Saved for you only · kept {historyDays} days
        </span>
        {messages.length > 0 &&
          (confirmClear ? (
            <span className={s.confirm} role="group" aria-label="Clear history">
              <span>Clear this conversation?</span>
              <Button
                variant="navy"
                size="sm"
                isDisabled={clear.isPending}
                onPress={() => {
                  clear.mutate();
                }}
              >
                {clear.isPending ? 'Clearing…' : 'Clear'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onPress={() => {
                  setConfirmClear(false);
                }}
              >
                Keep
              </Button>
            </span>
          ) : (
            <AriaButton
              className={s.textButton}
              onPress={() => {
                setConfirmClear(true);
              }}
            >
              Clear history
            </AriaButton>
          ))}
      </div>

      {conversation.isPending ? (
        <p className={s.note} role="status">
          Loading your conversation…
        </p>
      ) : (
        <ol className={s.log} aria-label="Conversation" aria-live="polite">
          {empty && (
            <li className={s.welcome}>
              <span className={s.botMark} aria-hidden="true">
                <Icon name="sparkle" size={18} />
              </span>
              <p className={s.welcomeHead}>
                {cleared
                  ? 'History cleared'
                  : `Hi ${greetingName}. Ask me about MyRewards or this page.`}
              </p>
              <p className={s.welcomeBody}>
                {cleared
                  ? 'Ask about this page, a rule or how to do something. Try a suggestion below.'
                  : 'I answer from MyRewards help and from what you can see on your screens. I can’t change awards. Automated.'}
              </p>
            </li>
          )}
          {messages.map((message) =>
            message.role === 'person' ? (
              <PersonBubble key={message.id} text={message.text} initials={initials} />
            ) : (
              <li key={message.id} className={s.message}>
                <span className={s.botMark} aria-hidden="true">
                  <Icon name="sparkle" size={16} />
                </span>
                <div className={cx(s.answer, message.answer?.unsure === true && s.unsure)}>
                  <span className="visually-hidden">Help: </span>
                  {message.answer === null ? (
                    <p className={s.para}>{message.text}</p>
                  ) : (
                    <>
                      {message.answer.paragraphs.map((p, i) => (
                        <p key={i} className={s.para}>
                          {p}
                        </p>
                      ))}
                      {message.answer.steps.length > 0 && (
                        <ol className={s.steps}>
                          {message.answer.steps.map((step, i) => (
                            <li key={i}>{step}</li>
                          ))}
                        </ol>
                      )}
                      <AnswerActions
                        answer={message.answer}
                        {...(showMe !== undefined && { showMe })}
                      />
                      {message.answer.topicIds.length > 0 && (
                        <ul className={s.chips} aria-label="Help topics this answer used">
                          {message.answer.topicIds.flatMap((id) => {
                            const topic = byId.get(id);
                            if (topic === undefined)
                              return [<li key={id}>This article is no longer available</li>];
                            return [
                              <li key={id}>
                                <AriaButton
                                  className={s.chip}
                                  onPress={() => {
                                    openTopic(id);
                                  }}
                                >
                                  <Icon name="book" size={14} />
                                  {topic.title}
                                </AriaButton>
                              </li>,
                            ];
                          })}
                        </ul>
                      )}
                      {message.answer.handoff !== null && (
                        <HandoffDraft
                          draft={message.answer.handoff}
                          conversationId={conversation.data?.conversationId ?? null}
                          contact={contact}
                        />
                      )}
                    </>
                  )}
                  <div className={s.meta}>
                    <span>AI-generated · check important details</span>
                    <span className={s.rating} role="group" aria-label="Rate this answer">
                      {(['up', 'down'] as const).map((rating) => (
                        <AriaButton
                          key={rating}
                          className={cx(s.thumb, message.feedback === rating && s.thumbOn)}
                          aria-pressed={message.feedback === rating}
                          aria-label={rating === 'up' ? 'Helpful' : 'Not helpful'}
                          isDisabled={rate.isPending}
                          onPress={() => {
                            rate.mutate({
                              id: message.id,
                              rating: message.feedback === rating ? null : rating,
                            });
                          }}
                        >
                          <Icon name={rating === 'up' ? 'thumbUp' : 'thumbDown'} size={16} />
                        </AriaButton>
                      ))}
                    </span>
                  </div>
                  {message.feedback !== null && (
                    <p className={s.thanks}>
                      {message.feedback === 'up'
                        ? 'Thanks. Marked as helpful.'
                        : 'Thanks. We’ll use this to improve Help.'}
                    </p>
                  )}
                </div>
              </li>
            ),
          )}
          {pendingQuestion !== null && (
            <>
              <PersonBubble text={pendingQuestion} initials={initials} />
              <li className={s.message}>
                <span className={s.botMark} aria-hidden="true">
                  <Icon name="sparkle" size={16} />
                </span>
                <p className={s.writing} role="status">
                  Help is writing…
                </p>
              </li>
            </>
          )}
        </ol>
      )}
      <div ref={endRef} />

      {error !== null && (
        <p className={s.error} role="alert">
          <Icon name="alert" size={16} /> {error}
        </p>
      )}

      {suggestions.length > 0 && (
        <div className={s.suggest}>
          <h3 className={cx('eyebrow', s.suggestHead)}>Suggested for this page</h3>
          <ul className={s.suggestList}>
            {suggestions.slice(0, SUGGESTION_LIMIT).map((q) => (
              <li key={q}>
                <AriaButton
                  className={s.suggestion}
                  isDisabled={ask.isPending || dictating}
                  onPress={() => {
                    submit(q);
                  }}
                >
                  {q}
                </AriaButton>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Form
        ref={formRef}
        className={s.form}
        onSubmit={(event) => {
          event.preventDefault();
          submit(draft);
        }}
      >
        {dictation.mic !== null && (
          <MicHelp help={dictation.mic} retryLabel="Try again" onRetry={dictation.start} />
        )}
        {dictation.notice !== null && (
          <p className={s.voiceNote} role="status">
            {dictation.notice}
          </p>
        )}
        <TextField
          label="Ask about MyRewards or this page"
          multiline
          // While dictating, the words fill the box as they come; it's theirs to edit after Done.
          value={dictating ? withWords(typed, dictation.heard) : draft}
          onChange={setDraft}
          isReadOnly={dictating}
          maxLength={QUESTION_MAX}
          {...(draft.length > QUESTION_MAX && !dictating && { errorMessage: TOO_LONG })}
          isInvalid={draft.length > QUESTION_MAX && !dictating}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit(draft);
            }
          }}
        />
        {dictation.phase !== 'idle' ? (
          <DictationStrip dictation={dictation} phase={dictation.phase} />
        ) : (
          <div className={s.formRow}>
            <p className={s.footnote}>
              AI-generated · check important details. Uses only what you can see in MyRewards.
              {remainingToday !== null && remainingToday <= 5 && (
                <>
                  {' '}
                  {remainingToday === 0
                    ? 'No questions left today.'
                    : `${String(remainingToday)} ${remainingToday === 1 ? 'question' : 'questions'} left today.`}
                </>
              )}
            </p>
            <span className={s.formActions}>
              {dictation.offered && (
                <DictateButton
                  isDisabled={ask.isPending}
                  onPress={() => {
                    setTyped(draft);
                    dictation.start();
                  }}
                />
              )}
              <Button
                type="submit"
                variant="navy"
                size="sm"
                icon={<Icon name="send" size={16} />}
                isDisabled={ask.isPending || draft.trim() === '' || draft.length > QUESTION_MAX}
              >
                Ask
              </Button>
            </span>
          </div>
        )}
      </Form>
    </div>
  );
}

/**
 * Under an answer: a how-to's start button (it opens the right page, AI4), then "Show me" when
 * the answer is about something on this page (APP-090) and the links it offers.
 */
function AnswerActions({
  answer,
  showMe,
}: {
  answer: HelpAnswer;
  showMe?: (anchor: HelpAnchorRef) => boolean;
}): ReactElement {
  const start = answer.start ?? null;
  const show = showMe === undefined ? null : (answer.show ?? null);
  return (
    <>
      {start !== null && (
        <p className={s.start}>
          <ButtonLink href={start.path} variant="navy" size="sm">
            {start.label}
          </ButtonLink>
        </p>
      )}
      {(answer.links.length > 0 || show !== null) && (
        <p className={s.links}>
          {show !== null && showMe !== undefined && (
            <ShowMeButton
              anchor={show}
              showMe={showMe}
              className={s.link}
              noteClassName={s.shownNote}
            />
          )}
          {answer.links.map((link) => (
            <Link key={link.path + link.label} href={link.path} className={s.link}>
              <Icon name="arrowRight" size={14} />
              {link.label}
            </Link>
          ))}
        </p>
      )}
    </>
  );
}

function PersonBubble({ text, initials }: { text: string; initials: string }): ReactElement {
  return (
    <li className={cx(s.message, s.mine)}>
      <span className={s.avatar} aria-hidden="true">
        {initials}
      </span>
      <p className={s.bubble}>
        <span className="visually-hidden">You: </span>
        {text}
      </p>
    </li>
  );
}

/** A message to the Stock Admin team the assistant drafted: nothing goes until Send. */
function HandoffDraft({
  draft,
  conversationId,
  contact,
}: {
  draft: { subject: string; message: string };
  conversationId: string | null;
  contact: HelpTopics['contact'] | null;
}): ReactElement {
  const [subject, setSubject] = useState(draft.subject);
  const [message, setMessage] = useState(draft.message);
  const [attach, setAttach] = useState(true);
  const [sentTo, setSentTo] = useState<readonly string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const attachId = useId();
  const send = useMutation({
    mutationFn: () =>
      sendHelpHandoff({
        subject: subject.trim(),
        message: message.trim(),
        conversationId: attach ? conversationId : null,
      }),
    onSuccess: (sent) => {
      setSentTo(sent.to.map((p) => p.name));
      setError(null);
    },
    onError: (failure) => {
      setError(aiProblemWords(failure, 'help'));
    },
  });

  if (sentTo !== null)
    return (
      <p className={s.sent} role="status">
        <Icon name="check" size={16} /> Sent to {namesInWords(sentTo)}. They’ll reply by email.
      </p>
    );

  const canSend = contact?.canWrite === true;
  return (
    <div className={s.handoff}>
      <p className={s.handoffHead}>
        Message to the Stock Admin team
        <span className={s.draftTag}>Draft · not sent</span>
      </p>
      <p className={s.handoffNote}>Check it. Nothing is sent until you press Send.</p>
      {canSend ? (
        <Form
          className={s.handoffForm}
          onSubmit={(event) => {
            event.preventDefault();
            if (subject.trim() === '' || message.trim() === '') return;
            send.mutate();
          }}
        >
          <TextField
            label="Subject"
            value={subject}
            onChange={setSubject}
            isRequired
            maxLength={200}
          />
          <TextField
            label="Message"
            multiline
            value={message}
            onChange={setMessage}
            isRequired
            maxLength={5000}
          />
          {conversationId !== null && (
            <label className={s.attach} htmlFor={attachId}>
              <input
                id={attachId}
                type="checkbox"
                checked={attach}
                onChange={(event) => {
                  setAttach(event.target.checked);
                }}
              />
              Attach this conversation
            </label>
          )}
          {error !== null && (
            <p className={s.error} role="alert">
              {error}
            </p>
          )}
          <Button type="submit" variant="navy" size="sm" isDisabled={send.isPending}>
            {send.isPending ? 'Sending…' : 'Send'}
          </Button>
        </Form>
      ) : contact?.email !== null && contact?.email !== undefined ? (
        <a
          className={s.link}
          href={`mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`}
        >
          <Icon name="mail" size={14} /> Email it to {contact.email}
        </a>
      ) : (
        <p className={s.handoffNote}>
          Sending from Help isn’t available here. Copy the message into an email to the Stock Admin
          team.
        </p>
      )}
    </div>
  );
}
